/**
 * ProviderDayScheduleModal
 * Simple modal for editing a single day's schedule (morning + afternoon + capacity).
 * Since the API does bulk upsert (replace all), we merge the edited day with
 * all other existing days before saving.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { WeekDay, WEEK_DAY_NAMES } from '../../../types/professionalSchedule';
import { ProviderWeeklySchedule } from '../../../types/provider';
import { providerService } from '../../../services/providerService';

interface ProviderDayScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  providerId: string | number;
  weekDay: WeekDay;
  allSchedules: ProviderWeeklySchedule[];
}

const ProviderDayScheduleModal: React.FC<ProviderDayScheduleModalProps> = ({
  open,
  onClose,
  onSaved,
  providerId,
  weekDay,
  allSchedules,
}) => {
  const [morningStart, setMorningStart] = useState('');
  const [morningEnd, setMorningEnd] = useState('');
  const [afternoonStart, setAfternoonStart] = useState('');
  const [afternoonEnd, setAfternoonEnd] = useState('');
  const [capacity, setCapacity] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from existing blocks for this day
  useEffect(() => {
    if (!open) return;
    setError(null);

    const blocks = allSchedules
      .filter(s => s.weekDay === weekDay)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    const morning = blocks[0];
    const afternoon = blocks.length > 1 ? blocks[1] : undefined;

    setMorningStart(morning?.startTime || '');
    setMorningEnd(morning?.endTime || '');
    setAfternoonStart(afternoon?.startTime || '');
    setAfternoonEnd(afternoon?.endTime || '');
    setCapacity(morning?.capacity != null ? String(morning.capacity) : '');
  }, [open, weekDay, allSchedules]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Build entries for the edited day
      const dayEntries: Omit<ProviderWeeklySchedule, 'id' | 'providerId' | 'isActive'>[] = [];
      if (morningStart && morningEnd) {
        dayEntries.push({
          weekDay,
          startTime: morningStart,
          endTime: morningEnd,
          capacity: capacity ? Number(capacity) : null,
        });
      }
      if (afternoonStart && afternoonEnd) {
        dayEntries.push({
          weekDay,
          startTime: afternoonStart,
          endTime: afternoonEnd,
          capacity: capacity ? Number(capacity) : null,
        });
      }

      // Keep entries from all other days unchanged
      const otherDayEntries = allSchedules
        .filter(s => s.weekDay !== weekDay)
        .map(s => ({
          weekDay: s.weekDay,
          startTime: s.startTime,
          endTime: s.endTime,
          capacity: s.capacity ?? null,
        }));

      await providerService.saveWeeklySchedule(providerId, [...otherDayEntries, ...dayEntries]);
      onSaved();
    } catch {
      setError('Erro ao salvar horários');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Horários — {WEEK_DAY_NAMES[weekDay]}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Manhã
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label="Início"
              type="time"
              value={morningStart}
              onChange={e => setMorningStart(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fim"
              type="time"
              value={morningEnd}
              onChange={e => setMorningEnd(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tarde
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label="Início"
              type="time"
              value={afternoonStart}
              onChange={e => setAfternoonStart(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fim"
              type="time"
              value={afternoonEnd}
              onChange={e => setAfternoonEnd(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>

          <TextField
            label="Capacidade"
            type="number"
            value={capacity}
            onChange={e => setCapacity(e.target.value)}
            size="small"
            fullWidth
            placeholder="Opcional"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          variant="contained"
          disabled={saving}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderDayScheduleModal;
